'use client';

import type { Filter } from '@dealerportal/common';
import {
  getFilterTranslationKey,
  translateOrFallback,
  translateUnitSuffix,
  useTranslations,
} from '@dealerportal/i18n/client';
import { upperWords } from '@dealerportal/utilities';
import truncate from 'lodash/truncate';
import React, { type FC } from 'react';

interface FilterPillProps {
  filterName: string;
  filterValue: string | boolean;
  onRemove: () => void;
}

const FilterPill: FC<FilterPillProps> = ({
  filterName,
  filterValue,
  onRemove,
}) => (
  <div className="ui-inline-flex ui-items-center ui-border-solid ui-border ui-border-gray-300 ui-rounded-md ui-px-2 ui-py-1 ui-mr-2 ui-mb-2">
    <span className="ui-text-sm ui-font-medium ui-mr-2">
      {filterValue === true || filterValue === false
        ? truncate(filterName, { length: 20 })
        : truncate(filterValue.toString(), { length: 20 })}
    </span>
    <button
      onClick={onRemove}
      className="ui-flex ui-items-center ui-justify-center ui-text-white ui-border-none ui-h-3 ui-w-3"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
        <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
      </svg>
    </button>
  </div>
);

export const FilterPills: FC<{ filters: Filter[]; dispatch: any }> = ({
  filters,
  dispatch,
}) => {
  const namespace = 'specs';
  const t = useTranslations(namespace);

  const translateFilterName = (filterName: string): string =>
    translateOrFallback(
      t,
      namespace,
      getFilterTranslationKey(filterName),
      filterName
    );

  const translateFilterValue = (filter: Filter, value: string): string => {
    const fallback =
      filter.displayNames[value] ?? upperWords(value.replace(/-/g, ' '));
    const messageKey = filter.displayNameKeys?.[value];
    const label = messageKey
      ? translateOrFallback(t, namespace, messageKey, fallback)
      : fallback;
    return translateUnitSuffix(t, namespace, label, filter.uom);
  };

  return (
    <div className="ui-flex ui-flex-wrap ui-items-center ui-justify-between">
      <div className="ui-flex ui-flex-wrap">
        {filters.map((filter) => (
          <React.Fragment key={filter.name}>
            {filter.enabledValues.map((value: string | boolean) => (
              <FilterPill
                key={String(value)}
                filterName={translateFilterName(filter.name)}
                filterValue={
                  value === '1'
                    ? translateFilterName(filter.name)
                    : translateFilterValue(filter, value.toString())
                }
                onRemove={() =>
                  dispatch({ type: 'REMOVE', filter: filter.name, value })
                }
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
